// src/lib/metrics/ratios.ts
/**
 * Performance ratios - Sharpe, Sortino, Calmar, Omega, etc.
 */

import type { Series } from './types';
import { TRADING_DAYS, DEFAULT_RISK_FREE_RATE } from './types';
import { filterValid, mean, stdDev, downsideDeviation } from './primitives';
import { totalReturn, annualizeReturn } from './returns';
import { maxDrawdownFromReturns } from './risk';

// ============================================================================
// RISK-ADJUSTED RETURN RATIOS
// ============================================================================

/**
 * Calculate Sharpe Ratio
 * (Mean Return - Risk-Free Rate) / Std Dev of Returns
 * 
 * @param returns - Array of period returns as decimals
 * @param riskFreeRate - Risk-free rate per period (default: 0)
 * @param annualize - Whether to annualize the ratio
 * @param tradingDays - Trading days per year for annualization
 * @returns Sharpe ratio (annualized if specified)
 */
export function sharpeRatio(
  returns: Series,
  riskFreeRate: number = DEFAULT_RISK_FREE_RATE,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const valid = filterValid(returns);
  if (valid.length < 2) return null;
  
  // Daily risk-free rate (if provided as annual)
  const dailyRf = riskFreeRate / tradingDays;
  
  // Excess returns
  const excessReturns = valid.map(r => r - dailyRf);
  
  const avgExcess = mean(excessReturns);
  const volatility = stdDev(excessReturns);
  
  if (avgExcess === null || volatility === null || volatility <= 0) {
    return null;
  }
  
  const dailySharpe = avgExcess / volatility;
  
  if (annualize) {
    return dailySharpe * Math.sqrt(tradingDays);
  }
  
  return dailySharpe;
}

/**
 * Calculate Sortino Ratio
 * (Mean Return - Risk-Free Rate) / Downside Deviation
 * 
 * Uses only negative returns for denominator, providing a 
 * better measure of downside risk-adjusted performance.
 */
export function sortinoRatio(
  returns: Series,
  riskFreeRate: number = DEFAULT_RISK_FREE_RATE,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const valid = filterValid(returns);
  if (valid.length < 2) return null;
  
  const dailyRf = riskFreeRate / tradingDays;
  const excessReturns = valid.map(r => r - dailyRf);
  
  const avgExcess = mean(excessReturns);
  const downside = downsideDeviation(excessReturns, 0);
  
  if (avgExcess === null || downside === null || downside <= 0) {
    return null;
  }
  
  const dailySortino = avgExcess / downside;
  
  if (annualize) {
    return dailySortino * Math.sqrt(tradingDays);
  }
  
  return dailySortino;
}

/**
 * Calculate Calmar Ratio
 * Annualized Return / |Max Drawdown|
 * 
 * Measures return relative to worst peak-to-trough decline.
 */
export function calmarRatio(
  returns: Series,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const valid = filterValid(returns);
  if (valid.length < 2) return null;
  
  // Calculate annualized return
  const total = totalReturn(valid);
  if (total === null) return null;
  
  const annReturn = annualizeReturn(total, valid.length, tradingDays);
  if (annReturn === null) return null;
  
  // Calculate max drawdown
  const maxDd = maxDrawdownFromReturns(valid);
  if (maxDd === null || maxDd >= 0) return null;
  
  const maxDdAbs = Math.abs(maxDd);
  
  // Guard against very small drawdowns
  if (maxDdAbs < 0.001) return null;
  
  const calmar = annReturn / maxDdAbs;
  
  // Sanity check - extreme values are likely errors
  if (!Number.isFinite(calmar) || Math.abs(calmar) > 100) {
    return null;
  }
  
  return calmar;
}

/**
 * Calculate Omega Ratio
 * Sum of positive returns / |Sum of negative returns|
 * 
 * @param returns - Array of returns
 * @param threshold - Return threshold (default: 0)
 */
export function omegaRatio(
  returns: Series,
  threshold: number = 0
): number | null {
  const valid = filterValid(returns);
  if (valid.length === 0) return null;
  
  const above = valid.filter(r => r > threshold);
  const below = valid.filter(r => r < threshold);
  
  const sumAbove = above.reduce((a, b) => a + (b - threshold), 0);
  const sumBelow = Math.abs(below.reduce((a, b) => a + (b - threshold), 0));
  
  if (sumBelow === 0) {
    return above.length > 0 ? Infinity : null;
  }
  
  return sumAbove / sumBelow;
}

/**
 * Calculate Information Ratio
 * (Portfolio Return - Benchmark Return) / Tracking Error
 * 
 * Measures risk-adjusted active return.
 */
export function informationRatio(
  returns: Series,
  benchmarkReturns: Series,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 2) return null;
  
  // Align from end
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  // Active returns
  const activeReturns = r.map((ret, i) => ret - b[i]!);
  
  const avgActive = mean(activeReturns);
  const trackingError = stdDev(activeReturns);
  
  if (avgActive === null || trackingError === null || trackingError <= 0) {
    return null;
  }
  
  const dailyIR = avgActive / trackingError;
  
  if (annualize) {
    return dailyIR * Math.sqrt(tradingDays);
  }
  
  return dailyIR;
}

/**
 * Calculate Treynor Ratio
 * (Portfolio Return - Risk-Free Rate) / Beta
 * 
 * Measures excess return per unit of systematic risk.
 */
export function treynorRatio(
  returns: Series,
  beta: number,
  riskFreeRate: number = DEFAULT_RISK_FREE_RATE,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  if (!Number.isFinite(beta) || beta === 0) return null;
  
  const valid = filterValid(returns);
  if (valid.length < 2) return null;
  
  const dailyRf = riskFreeRate / tradingDays;
  const excessReturns = valid.map(r => r - dailyRf);
  
  const avgExcess = mean(excessReturns);
  if (avgExcess === null) return null;
  
  const dailyTreynor = avgExcess / beta;
  
  if (annualize) {
    return dailyTreynor * tradingDays;
  }
  
  return dailyTreynor;
}

// ============================================================================
// CAPTURE RATIOS
// ============================================================================

/**
 * Calculate upside capture ratio
 * Measures how well an asset captures benchmark gains
 * 
 * @returns Ratio where 1.0 = same as benchmark, >1 = outperforms up moves
 */
export function upsideCapture(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 5) return null;
  
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  // Filter for positive benchmark days
  const upDays: { r: number; b: number }[] = [];
  for (let i = 0; i < n; i++) {
    if (b[i]! > 0) {
      upDays.push({ r: r[i]!, b: b[i]! });
    }
  }
  
  if (upDays.length < 2) return null;
  
  // Compound returns
  let assetCumulative = 1;
  let benchCumulative = 1;
  
  for (const day of upDays) {
    assetCumulative *= (1 + day.r);
    benchCumulative *= (1 + day.b);
  }
  
  const assetReturn = assetCumulative - 1;
  const benchReturn = benchCumulative - 1;
  
  if (benchReturn === 0) return null;
  
  return assetReturn / benchReturn;
}

/**
 * Calculate downside capture ratio
 * Measures how well an asset avoids benchmark losses
 * 
 * @returns Ratio where 1.0 = same as benchmark, <1 = loses less
 */
export function downsideCapture(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 5) return null;
  
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  // Filter for negative benchmark days
  const downDays: { r: number; b: number }[] = [];
  for (let i = 0; i < n; i++) {
    if (b[i]! < 0) {
      downDays.push({ r: r[i]!, b: b[i]! });
    }
  }
  
  if (downDays.length < 2) return null;
  
  // Compound returns
  let assetCumulative = 1;
  let benchCumulative = 1;
  
  for (const day of downDays) {
    assetCumulative *= (1 + day.r);
    benchCumulative *= (1 + day.b);
  }
  
  const assetReturn = assetCumulative - 1;
  const benchReturn = benchCumulative - 1;
  
  if (benchReturn === 0) return null;
  
  return assetReturn / benchReturn;
}

/**
 * Calculate capture spread
 * Upside Capture - Downside Capture
 * 
 * @returns Positive = good (captures more upside than downside)
 *          Downside capture is already positive (ratio of negative/negative),
 *          where <1 means less downside captured (good).
 */
export function captureSpread(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const up = upsideCapture(returns, benchmarkReturns);
  const down = downsideCapture(returns, benchmarkReturns);
  
  if (up === null || down === null) return null;
  
  return up - down;
}

// ============================================================================
// HIT RATE
// ============================================================================

/**
 * Calculate up/down hit rates vs benchmark
 * 
 * @returns Hit rates: what % of up/down benchmark days does asset follow
 */
export function hitRates(
  returns: Series,
  benchmarkReturns: Series
): { upHitRate: number | null; downHitRate: number | null } {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 5) return { upHitRate: null, downHitRate: null };
  
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  let upDays = 0;
  let upHits = 0;
  let downDays = 0;
  let downHits = 0;
  
  for (let i = 0; i < n; i++) {
    if (b[i]! > 0) {
      upDays++;
      if (r[i]! > 0) upHits++;
    } else if (b[i]! < 0) {
      downDays++;
      if (r[i]! < 0) downHits++;
    }
  }
  
  return {
    upHitRate: upDays > 0 ? upHits / upDays : null,
    downHitRate: downDays > 0 ? downHits / downDays : null,
  };
}

